// Image optimization utilities for SEO and performance

export interface ImageSEOData {
  src: string;
  alt: string;
  title?: string;
  caption?: string;
  width?: number;
  height?: number;
  priority?: boolean;
}

/**
 * Generate SEO-optimized alt text for restaurant images
 */
export const generateAltText = (
  imageType: 'food' | 'interior' | 'exterior' | 'staff' | 'hero',
  itemName?: string,
  location?: string
): string => {
  const baseLocation = location || 'Rossopomodoro Reykjavik';

  switch (imageType) {
    case 'food':
      return itemName
        ? `${itemName} authentic Italian dish at ${baseLocation} best Italian restaurant downtown Reykjavik Laugavegur`
        : `Authentic Italian cuisine Neapolitan pizza pasta ${baseLocation} best Italian restaurant downtown Reykjavik`;

    case 'interior':
      return `${baseLocation} interior dining room cozy Italian restaurant atmosphere downtown Reykjavik Laugavegur best Italian food`;

    case 'exterior':
      return `${baseLocation} exterior Laugavegur downtown Reykjavik best Italian restaurant authentic Neapolitan pizza`;

    case 'staff':
      return `Professional Italian chefs staff ${baseLocation} authentic Italian cooking best restaurant downtown Reykjavik`;

    case 'hero':
      return `${baseLocation} authentic Neapolitan pizza Italian cuisine downtown Reykjavik since 2006 best Italian restaurant Laugavegur`;

    default:
      return `${baseLocation} best Italian restaurant downtown Reykjavik authentic Neapolitan pizza pasta`;
  }
};

/**
 * Generate structured data for images
 */
export const generateImageStructuredData = (images: ImageSEOData[]) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'ImageGallery',
    name: 'Rossopomodoro Reykjavik Restaurant Images',
    description:
      'Photo gallery of authentic Italian cuisine, restaurant interior, and dining experience',
    image: images.map(img => ({
      '@type': 'ImageObject',
      url: img.src,
      name: img.alt,
      description: img.caption || img.alt,
      width: img.width,
      height: img.height,
      caption: img.caption,
    })),
  };
};

/**
 * Optimize Pexels URLs for better performance
 */
export const optimizePexelsUrl = (
  originalUrl: string,
  width: number = 1200,
  height?: number,
  quality: number = 85
): string => {
  if (!originalUrl.includes('pexels.com')) {
    return originalUrl;
  }

  const baseUrl = originalUrl.split('?')[0];
  const params = new URLSearchParams({
    auto: 'compress',
    cs: 'tinysrgb',
    w: width.toString(),
    dpr: '1',
  });

  if (height) {
    params.set('h', height.toString());
  }

  if (quality !== 85) {
    params.set('q', quality.toString());
  }

  return `${baseUrl}?${params.toString()}`;
};

/**
 * Generate responsive image srcset for Pexels images
 */
export const generatePexelsSrcSet = (originalUrl: string): string => {
  if (!originalUrl.includes('pexels.com')) {
    return originalUrl;
  }

  const baseUrl = originalUrl.split('?')[0];

  return [
    `${baseUrl}?auto=compress&cs=tinysrgb&w=400&dpr=1 400w`,
    `${baseUrl}?auto=compress&cs=tinysrgb&w=800&dpr=1 800w`,
    `${baseUrl}?auto=compress&cs=tinysrgb&w=1200&dpr=1 1200w`,
    `${baseUrl}?auto=compress&cs=tinysrgb&w=1600&dpr=1 1600w`,
    `${baseUrl}?auto=compress&cs=tinysrgb&w=2000&dpr=1 2000w`,
  ].join(', ');
};

/**
 * SEO-friendly filename generator
 */
export const generateSEOFilename = (
  originalName: string,
  itemType: string,
  restaurantName: string = 'rossopomodoro-reykjavik'
): string => {
  const cleanName = originalName
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();

  return `${restaurantName}-${itemType}-${cleanName}`;
};

/**
 * Image compression recommendations
 */
export const getCompressionSettings = (imageType: 'hero' | 'menu' | 'gallery' | 'thumbnail') => {
  switch (imageType) {
    case 'hero':
      return {
        quality: 85,
        format: 'webp',
        maxWidth: 1920,
        maxHeight: 1080,
      };
    case 'menu':
      return {
        quality: 80,
        format: 'webp',
        maxWidth: 800,
        maxHeight: 600,
      };
    case 'gallery':
      return {
        quality: 75,
        format: 'webp',
        maxWidth: 1200,
        maxHeight: 900,
      };
    case 'thumbnail':
      return {
        quality: 70,
        format: 'webp',
        maxWidth: 400,
        maxHeight: 300,
      };
    default:
      return {
        quality: 80,
        format: 'webp',
        maxWidth: 1200,
        maxHeight: 900,
      };
  }
};
